<!-- Responsibility: Implement atlas panel behavior for the ui/panels surface.
Scope: Owns ui/panels concerns only; cross-surface orchestration is excluded. -->
<atlas-panel>
  <div class="panel-content">
    <header>
      <div>
        <p>ATLAS</p>
        <h5>{ state.roomName }</h5>
      </div>
      <nav aria-label="Atlas panel actions">
        <button type="button" onclick={ openAtlasDialog }>OPEN FULL MAP</button>
      </nav>
    </header>

    <atlas-map
      if={ state.atlas }
      atlas={ state.atlas }
      location={ state.location }
      current-room={ state.currentRoom }
      current-vision-url={ state.currentVisionUrl }
      invert-zoom={ state.invertMapScroll }
    ></atlas-map>

    <section if={ state.routeSummaries.length } data-block="routes">
      <header>
        <strong>KNOWN PASSAGES</strong>
        <span>{ state.routeSummaries.length } lanes</span>
      </header>
      <ul>
        <li each={ route in state.routeSummaries }>
          <button type="button" onclick={ () => openRouteDialog(route) }>
            <strong>{ route.title }</strong>
            <span>{ route.meta }</span>
          </button>
        </li>
      </ul>
    </section>

    <div class="world-npc-bar" if={ state.worldNpcNodes.length }>
      <span class="world-npc-label">KNOWN PRESENCES ELSEWHERE:</span>
      <span each={ entry in state.worldNpcNodes } class="world-npc-chip">
        <span class="world-npc-dot"></span>{ entry.shortName }
      </span>
    </div>

    <h5 class="sub-title first-block">WHO IS HERE</h5>
    <div class="entity-list" if={ state.npcs.length }>
      <npc-card each={ npc in state.npcs } npc={ npc }></npc-card>
    </div>
    <p if={ !state.npcs.length } class="empty-note">No one known.</p>

    <h5 class="sub-title">WHAT IS HERE</h5>
    <div class="entity-list" if={ state.roomItems.length }>
      <item-card
        each={ name in state.roomItems }
        name={ name }
        location="room"
        accent-color="var(--brand-primary)"
      ></item-card>
    </div>
    <p if={ !state.roomItems.length } class="empty-note">Nothing certain.</p>

    <div class="clock-display" if={ state.timeStr }>
      { state.dayStr }
      <span class="clock-time">{ state.timeStr }</span>
    </div>
  </div>

  <script>
    import { dispatcher } from '../../app-states.js'
    import { TOPICS } from "@anyquest/shared-core"
    import * as gameStore from '../../game-store.js'
    import { loadAIConfig } from '../../ai/config.js'
    import { formatDay, formatClock } from '../../engine/temporal.js'
    import { buildInfoDialog } from '../dialog/dialog-openers.ts'
    import AtlasMap from '../components/atlas-map.riot'
    import NpcCard from '../components/npc-card.riot'
    import ItemCard from '../components/item-card.riot'

    export default {
      components: { AtlasMap, NpcCard, ItemCard },

      state: {
        atlas:          null,
        location:       { x: 0, y: 0, z: 0 },
        currentRoom:    null,
        currentVisionUrl: '',
        roomName:       '',
        npcs:           [],
        roomItems:      [],
        dayStr:         '',
        timeStr:        '',
        worldNpcNodes:  [], // [{ key, names, shortName }] — NPC clusters per atlas node
        routeSummaries: [],
        invertMapScroll: false,
      },

      onMounted() {
        this.handles = [
          dispatcher.on(TOPICS.game.loaded,         (_e, gs) => { this.syncFrom(gs) }),
          dispatcher.on(TOPICS.game.started,        (_e, gs) => { this.syncFrom(gs) }),
          dispatcher.on(TOPICS.game.actionComplete, ()       => { if (gameStore.state) this.syncFrom(gameStore.state) }),
          dispatcher.on(TOPICS.game.roomChanged,    ()       => { if (gameStore.state) this.syncFrom(gameStore.state) }),
          dispatcher.on(TOPICS.world.ticked,        ()       => { if (gameStore.state) this.syncFrom(gameStore.state) }),
          dispatcher.on(TOPICS.world.npcMoved,      ()       => { if (gameStore.state) this.syncFrom(gameStore.state) }),
          dispatcher.on(TOPICS.ui.aiConfigSaved,    (_e, cfg) => { this.update({ invertMapScroll: !!cfg?.invertMapScroll }) }),
        ]
        if (gameStore.state) this.syncFrom(gameStore.state)
      },

      onUnmounted() {
        this.handles?.forEach(h => dispatcher.off(h))
      },

      buildWorldNpcNodes(gs) {
        // Group world NPCs by atlas node location, excluding player's current node
        const playerLoc = `${gs.player.location.x}_${gs.player.location.y}`
        const byNode = {}
        for (const npc of gs.world?.npcs ?? []) {
          if (npc.location === playerLoc) continue // shown in ENTITIES list
          if (!byNode[npc.location]) byNode[npc.location] = []
          byNode[npc.location].push(npc.name)
        }
        return Object.entries(byNode).map(([key, names]) => ({
          key,
          names,
          shortName: names.length === 1 ? names[0].split(' ')[0] : `${names.length} people`,
          summary: `${names.join(', ')} @ ${key}`,
        }))
      },

      buildRouteSummaries(gs) {
        const playerX = gs.player.location.x
        const playerY = gs.player.location.y
        const byDirection = []
        const seen = new Set()

        for (const edge of gs.atlas?.edges ?? []) {
          let nx = null
          let ny = null
          if (edge.x1 === playerX && edge.y1 === playerY) {
            nx = edge.x2
            ny = edge.y2
          } else if (edge.x2 === playerX && edge.y2 === playerY) {
            nx = edge.x1
            ny = edge.y1
          }
          if (nx == null || ny == null) continue
          const key = `${nx}_${ny}`
          if (seen.has(key)) continue
          seen.add(key)

          const dx = nx - playerX
          const dy = ny - playerY
          const heading = dx === 1 ? 'East' : dx === -1 ? 'West' : dy === 1 ? 'North' : 'South'
          const node = gs.atlas.nodes[key]
          const title = node?.flags?.presentation === 'passage'
            ? `${heading} passage`
            : `${heading} route`
          byDirection.push({
            key,
            direction: heading.toLowerCase(),
            nodeName: node?.name ?? 'Unknown route',
            title,
            meta: `${node?.name ?? 'Unknown route'} · ${node?.flags?.remote_access ? 'remote access' : 'direct path'}`,
            input: `Go ${heading.toLowerCase()}`,
          })
        }

        return byDirection
      },

      syncFrom(gs) {
        const cfg = loadAIConfig()
        this.update({
          atlas:         gs.atlas,
          location:      gs.player.location,
          currentRoom:   gs.currentRoom,
          roomName:      gs.currentRoom.name,
          currentVisionUrl: gs.visionUrl ?? '',
          npcs:          gs.currentRoom.npcs,
          roomItems:     gs.currentRoom.items,
          dayStr:        formatDay(gs.world.clock),
          timeStr:       formatClock(gs.world.clock),
          worldNpcNodes: this.buildWorldNpcNodes(gs),
          routeSummaries: this.buildRouteSummaries(gs),
          invertMapScroll: !!cfg.invertMapScroll,
        })
      },

      openRouteDialog(route) {
        if (!route) return
        dispatcher.trigger('atlas-panel', TOPICS.ui.dialogOpen, buildInfoDialog({
          options: {
            title: route.title,
            desc: `This passage leads ${route.direction}. ${route.nodeName}.`,
            meta: route.meta,
            actions: [{ label: `GO ${route.direction.toUpperCase()}`, input: route.input }],
          },
        }))
      },

      openAtlasDialog() {
        dispatcher.trigger('atlas-panel', TOPICS.ui.dialogOpen, {
          content: 'atlas-dialog',
          data: {
            atlas: this.state.atlas,
            location: this.state.location,
            currentRoom: this.state.currentRoom,
            currentVisionUrl: this.state.currentVisionUrl,
            roomName: this.state.roomName,
            routeSummaries: this.state.routeSummaries,
            worldNpcNodes: this.state.worldNpcNodes,
            onOpenRoute: (route) => this.openRouteDialog(route),
          },
          options: {
            title: 'FULL ATLAS',
            desc: 'Study the known passages, presences, and room links from one dedicated map surface.',
            variant: 'atlas',
          },
        })
      },
    }
  </script>

  <style>
    .panel-content > header {
      display: flex;
      align-items: start;
      justify-content: space-between;
      gap: 0.9rem;
      margin-bottom: var(--space-md);
    }

    .panel-content > header > div {
      display: grid;
      gap: var(--space-xs);
    }

    .panel-content > header p,
    .panel-content > header h5 {
      margin: 0;
    }

    .panel-content > header p {
      color: var(--gold);
      font-size: 0.68rem;
      letter-spacing: 0.16em;
      font-weight: 700;
    }

    .panel-content > header h5 {
      color: var(--text-prose, var(--text-prose));
      font-size: var(--space-md);
      line-height: 1.05;
      letter-spacing: 0.04em;
    }

    .panel-content > header > nav > button {
      min-height: 2.4rem;
      padding: var(--space-xs) 0.88rem;
      border-radius: 62.4375rem;
      border: 1px solid color-mix(in srgb, var(--gold) var(--alpha-ghost), transparent);
      background: color-mix(in srgb, rgba(7, 11, 18, 0.9) var(--alpha-high), transparent);
      color: var(--text-prose, var(--text-prose));
      font: inherit;
      font-size: var(--space-sm);
      letter-spacing: 0.12em;
      cursor: pointer;
      transition: transform 0.18s ease, border-color 0.18s ease;
    }

    .panel-content > header > nav > button:hover,
    .panel-content > header > nav > button:focus-visible {
      outline: none;
      transform: translateY(-1px);
      border-color: color-mix(in srgb, var(--gold) var(--alpha-mid), transparent);
    }

    .panel-content > section[data-block='routes'] {
      display: grid;
      gap: 0.65rem;
      margin: var(--space-md) 0 1.15rem;
      padding: 0.85rem 0.9rem;
      border: 1px solid color-mix(in srgb, var(--brand-primary, var(--brand-primary)) var(--alpha-low), transparent);
      border-radius: var(--space-md);
      background: linear-gradient(180deg, color-mix(in srgb, var(--surface-card-2) var(--alpha-high), var(--bg)), color-mix(in srgb, var(--surface-card-1) var(--alpha-high), var(--bg)));
    }

    .panel-content > section[data-block='routes'] > header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--space-xs);
    }

    .panel-content > section[data-block='routes'] > header > strong,
    .panel-content > section[data-block='routes'] > header > span {
      margin: 0;
      font-size: 0.66rem;
      letter-spacing: 0.13em;
    }

    .panel-content > section[data-block='routes'] > header > strong {
      color: color-mix(in srgb, var(--brand-primary, #aeb9ff) var(--alpha-high), var(--text-prose));
    }

    .panel-content > section[data-block='routes'] > header > span {
      color: var(--text-dim, #93a8c2);
    }

    .panel-content > section[data-block='routes'] > ul {
      list-style: none;
      margin: 0;
      padding: 0;
      display: grid;
      gap: var(--space-xs);
    }

    .panel-content > section[data-block='routes'] > ul > li {
      animation: route-row-in 0.34s ease-out both;
    }

    .panel-content > section[data-block='routes'] > ul > li > button {
      width: 100%;
      display: grid;
      gap: 0.12rem;
      padding: var(--space-xs) var(--space-sm);
      border-radius: 0.85rem;
      border: 1px solid color-mix(in srgb, var(--border) var(--alpha-subtle), transparent);
      background: color-mix(in srgb, rgba(5, 10, 18, 0.74) var(--alpha-high), transparent);
      color: inherit;
      font: inherit;
      text-align: left;
      cursor: pointer;
      transition: transform 0.18s ease, border-color 0.18s ease;
    }

    .panel-content > section[data-block='routes'] > ul > li > button:hover,
    .panel-content > section[data-block='routes'] > ul > li > button:focus-visible {
      transform: translateY(-1px);
      border-color: color-mix(in srgb, var(--gold) var(--alpha-low), transparent);
      outline: none;
    }

    .panel-content > section[data-block='routes'] > ul > li > button > strong,
    .panel-content > section[data-block='routes'] > ul > li > button > span {
      margin: 0;
    }

    .panel-content > section[data-block='routes'] > ul > li > button > strong {
      color: var(--text-prose, var(--text-prose));
      font-size: 0.82rem;
      letter-spacing: 0.04em;
    }

    .panel-content > section[data-block='routes'] > ul > li > button > span {
      color: var(--text-dim, #93a8c2);
      font-size: var(--space-sm);
      line-height: 1.45;
    }

    .world-npc-bar {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--space-xs);
      margin-bottom: var(--space-sm);
      padding: var(--space-xs) var(--space-xs);
      background: color-mix(in srgb, var(--brand-primary, var(--brand-primary)) var(--alpha-subtle), transparent);
      border: 1px solid color-mix(in srgb, var(--brand-primary, var(--brand-primary)) var(--alpha-low), transparent);
      border-radius: 0.25rem;
    }
    .world-npc-label {
      font-size: 0.6rem;
      letter-spacing: 0.1em;
      color: var(--text-muted, #6b7280);
    }
    .world-npc-chip {
      display: flex;
      align-items: center;
      gap: var(--space-xs);
      font-size: 0.65rem;
      color: color-mix(in srgb, var(--brand-primary, var(--brand-primary)) var(--alpha-high), var(--text));
      cursor: default;
    }
    .world-npc-dot {
      width: 0.375rem;
      height: 0.375rem;
      border-radius: 50%;
      background: var(--brand-primary, var(--brand-primary));
      opacity: 0.7;
      flex-shrink: 0;
    }

    .first-block {
      margin-top: 0;
    }
  </style>
</atlas-panel>
